import {
  createContext,
  createElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { api, ApiError } from '../api/client'
import { GlobalAudio } from '@game/systems/AudioSystem'
import type { PlayerProfile, PlayerProgress } from '@shared/types'

const TOKEN_KEY = 'cs_token'
const PROFILE_KEY = 'cs_profile'

interface AuthContextValue {
  token: string | null
  profile: PlayerProfile | null
  progress: PlayerProgress | null
  login: (username: string, password: string) => Promise<void>
  googleLogin: (idToken: string) => Promise<void>
  logout: () => void
  setProgress: (p: PlayerProgress | null) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY))
  const [profile, setProfile] = useState<PlayerProfile | null>(() => {
    const raw = localStorage.getItem(PROFILE_KEY)
    return raw ? JSON.parse(raw) : null
  })
  const [progress, setProgress] = useState<PlayerProgress | null>(null)

  useEffect(() => {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  }, [token])

  useEffect(() => {
    if (profile) localStorage.setItem(PROFILE_KEY, JSON.stringify(profile))
    else localStorage.removeItem(PROFILE_KEY)
  }, [profile])

  // Hidrata progress do servidor quando temos token mas não estado em memória
  // (refresh da página, deep link em /play/:id, etc.).
  useEffect(() => {
    if (!token || progress) return
    let cancelled = false
    api.profile()
      .then((res) => {
        if (cancelled || !res.ok) return
        setProfile(res.user)
        setProgress(res.progress)
      })
      .catch((err) => {
        if (cancelled) return
        console.error('Falha ao hidratar perfil:', err)
        if (err instanceof ApiError && err.status === 401) {
          // Token inválido/expirado — limpa sessão para forçar relogin via RequireAuth.
          localStorage.removeItem(TOKEN_KEY)
          localStorage.removeItem(PROFILE_KEY)
          setToken(null)
          setProfile(null)
        }
      })
    return () => {
      cancelled = true
    }
  }, [token, progress])

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.login(username, password)
    localStorage.setItem(TOKEN_KEY, res.token)
    localStorage.setItem(PROFILE_KEY, JSON.stringify(res.user))
    setToken(res.token)
    setProfile(res.user)
    setProgress(res.progress)
  }, [])

  const googleLogin = useCallback(async (idToken: string) => {
    const res = await api.googleLogin(idToken)
    localStorage.setItem(TOKEN_KEY, res.token)
    localStorage.setItem(PROFILE_KEY, JSON.stringify(res.user))
    setToken(res.token)
    setProfile(res.user)
    setProgress(res.progress)
  }, [])

  const logout = useCallback(() => {
    // Corta qualquer trilha tocando (menu/fase) imediatamente — o áudio vive
    // no GlobalAudio (Howler), independente do React, e não para sozinho.
    GlobalAudio.stopAll({ fadeMs: 0 })
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(PROFILE_KEY)
    setToken(null)
    setProfile(null)
    setProgress(null)
  }, [])

  const value: AuthContextValue = { token, profile, progress, login, googleLogin, logout, setProgress }
  return createElement(AuthContext.Provider, { value }, children)
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth deve estar dentro de <AuthProvider>')
  return ctx
}
